import React, { useContext, useEffect, useState } from 'react';
import IPage from '../../../interfaces/IPage';
import { Container } from './EmplyeeSearch.styped';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';
import { useValidateSchema } from '../../../hooks/providers/useValidateSchema';
import IEmployee from '../../../interfaces/IEmployee';
import IFormError from '../../../interfaces/IFormError';
import { useEmployeeService } from '../../../hooks/services/useEmployeeService';
import debounce from 'lodash.debounce';
import * as yup from 'yup';
import PageTitle from '../../../components/page-title/PageTitle';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ThemeOptionContext from '../../../context/theme-option/ThemeOptionContext';
import { hasAccessToAction } from '../../../util/hasAccessTo';
import UserContext from '../../../context/user/UserContext';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import SearchIcon from '@mui/icons-material/Search';
import { sdsFontWeights, sdsGrayscale, sdsTypeScale } from '../../../theme/sds-tokens';
import useCpfCnpjFormat from '../../../hooks/util/useCpfCnpjFormat';

interface IProps extends IPage {}

const searchInputSx = {
  '& .MuiAutocomplete-inputRoot': { paddingTop: 0, paddingBottom: 0, paddingLeft: '10px' },
  '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
  },
};

const FIELD_MAX_WIDTH = 480;

const getInitials = (name?: string): string => {
  const parts = `${name ?? ''}`.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '';
  }
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${parts[0][0]}${last}`.toUpperCase();
};

const EmployeeSearch = (props: IProps) => {
  window.document.title = props.title;

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly IEmployee[]>([]);
  const loadingInput = open;

  const [employee, setEmployee] = useState<IEmployee | null>(null);
  const request = useEmployeeService();

  const { state } = useContext(UserContext);

  const toastr = useToastrVanila();
  const validation = useValidateSchema();
  const [errors, setErrors] = useState<IFormError | null>();
  const navigate = useNavigate();

  const { state: themeState } = useContext(ThemeOptionContext);

  const { formatCpf } = useCpfCnpjFormat();

  const secondaryText = themeState.isDark ? sdsGrayscale[40] : sdsGrayscale[60];

  useEffect(() => {
    let active = true;

    if (!loadingInput) {
      return undefined;
    }

    (async () => {
      if (active) {
        await loadEmployees(' - ');
      }
    })();

    return () => {
      active = false;
    };
  }, [loadingInput]);

  useEffect(() => {
    setErrors(null);
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  const loadEmployees = debounce(async (filter = ' ') => {
    setLoading(true);
    await request
      .listForMedicalCertificateSearch(filter)
      .then(employees => {
        setOptions(employees);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(`Erro ao carregar os dados: ${error.message}`);
        setLoading(false);
      });
  }, 500);

  const schema = yup.object({
    cpf: yup
      .string()
      .required('Campo obrigatório')
      .min(1, 'Campo obrigatório.'),
  });

  const hancleClick = async () => {
    const result = await validation.validate(schema, employee || {});
    setErrors(result.errors);

    if (!result.isValid) {
      return;
    }
    if(hasAccessToAction(["cadastrar-documento-atestado-web"], state.resourcesWithActions, "Editar")) {
      navigate(`/cadastrar-documento/0/${employee?.id}/new`);
    } else {
      toastr.error('Acesso negado. Verificar com o administrador do sistema.');
    }
  };

  return (
    <Container>
      <PageTitle
        title="Cadastro de atestado"
        icon={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
            }}
          >
            <NoteAddIcon fontSize="small" />
          </Box>
        }
        subtitle={
          <Typography variant="helper" sx={{ display: 'block', color: secondaryText }}>
            Selecione o colaborador para iniciar o lançamento
          </Typography>
        }
      />
      {loading && <LinearProgress />}
      <Paper sx={{ marginTop: 2 }}>
        <Box sx={{ padding: 2 }}>
          <Typography
            variant="sectionTitle"
            component="label"
            htmlFor="employee-search"
            sx={{ display: 'block', marginBottom: 1 }}
          >
            Colaborador{' '}
            <Box component="span" sx={{ color: 'error.main' }}>
              *
            </Box>
          </Typography>

          <Box sx={{ maxWidth: FIELD_MAX_WIDTH }}>
            <Autocomplete
              open={open}
              value={employee}
              onChange={(e: any, newValue) => {
                const castedEmployee = { ...newValue } as IEmployee;
                setEmployee({ ...castedEmployee });
              }}
              onOpen={() => {
                setOpen(true);
                setEmployee(null);
              }}
              onClose={() => {
                setOpen(false);
              }}
              isOptionEqualToValue={(option, value) => {
                return option?.name === value?.name;
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box
                    sx={{
                      flexGrow: 1,
                      '& span': {
                        color: option.situation === 7 ? '#c0392b' : themeState.isDark ? '#F8FFFF' : '#212121',
                      },
                    }}
                  >
                    <span>
                      {option?.cpf
                        ? `${option.cpf} - ${option.code} - ${option.name} ${option.situation === 7 ? ' - Demitido' : ''}`
                        : ''}
                    </span>
                  </Box>
                </li>
              )}
              getOptionLabel={option =>
                option?.cpf
                  ? `${option.cpf} - ${option.code} -  ${option.name} ${option.situation === 7 ? ' - Demitido' : ''}`
                  : ''
              }
              options={options}
              loading={loading}
              renderInput={params => (
                <TextField
                  {...params}
                  id="employee-search"
                  sx={{
                    ...searchInputSx,
                    '& .MuiFormHelperText-root:not(.Mui-error)': { color: secondaryText },
                  }}
                  placeholder="Nome, matrícula ou CPF"
                  error={Boolean(errors?.cpf)}
                  helperText={errors?.cpf}
                  onChange={e => {
                    loadEmployees(e.target.value);
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <React.Fragment>
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: secondaryText }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </React.Fragment>
                    ),
                    endAdornment: (
                      <React.Fragment>
                        {loading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                    style: {
                      color: employee && employee.situation === 7 ? '#c0392b' : themeState.isDark ? '#F8FFFF' : '#212121',
                    },
                  }}
                />
              )}
            />
          </Box>

          {employee?.id && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                maxWidth: FIELD_MAX_WIDTH,
                marginTop: 2,
                padding: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  fontSize: sdsTypeScale.body.fontSize,
                  fontWeight: sdsFontWeights.semibold,
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                  color: 'text.primary',
                }}
              >
                {getInitials(employee.name)}
              </Avatar>
              <Box>
                <Typography variant="sectionTitle" sx={{ display: 'block' }}>
                  {employee.name}
                </Typography>
                <Typography variant="helper" sx={{ display: 'block', color: secondaryText }}>
                  {`Matrícula ${employee.code ?? '-'} · CPF ${formatCpf(`${employee.cpf ?? ''}`)}`}
                </Typography>
                {employee.company?.name && (
                  <Typography variant="helper" sx={{ display: 'block', color: secondaryText }}>
                    {`${employee.company.code ? `${employee.company.code} – ` : ''}${employee.company.name}`}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }}>
          <Button variant="contained" onClick={hancleClick}>
            Cadastrar
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default EmployeeSearch;
